import { useRef, useState } from "react";
import { adminUploadFile, resolveAssetUrl } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, X } from "@phosphor-icons/react";

export default function ImageUpload({ value, onChange, label = "Εικόνα", testid = "image-upload", accept = "image/*,video/*" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Το αρχείο είναι πολύ μεγάλο (>20MB). Συμπίεσέ το πρώτα.");
      return;
    }
    setUploading(true);
    try {
      const { url } = await adminUploadFile(file);
      onChange(url);
      toast.success("Αρχείο ανέβηκε");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Αποτυχία ανεβάσματος";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = "";
  };

  const isVideo = value && (value.endsWith(".mp4") || value.endsWith(".webm") || value.endsWith(".mov"));

  return (
    <div data-testid={testid} className="space-y-2">
      <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">{label}</span>
      <div className="flex items-start gap-4">
        {value ? (
          <div className="relative w-32 h-32 border border-white/15 bg-[#1A1A1A] overflow-hidden flex-shrink-0">
            {isVideo ? (
              <video src={resolveAssetUrl(value)} className="w-full h-full object-cover" muted />
            ) : (
              <img src={resolveAssetUrl(value)} alt="preview" className="w-full h-full object-cover" />
            )}
            <button
              type="button"
              onClick={() => onChange("")}
              data-testid={`${testid}-remove`}
              className="absolute top-1 right-1 w-6 h-6 bg-black/70 border border-white/20 flex items-center justify-center hover:bg-[#E50000] hover:border-[#E50000]"
            >
              <X size={12} weight="bold" />
            </button>
          </div>
        ) : (
          <div className="w-32 h-32 border border-dashed border-white/15 bg-[#1A1A1A] flex items-center justify-center text-white/30 text-xs flex-shrink-0">
            no file
          </div>
        )}

        <div className="flex-1 space-y-2">
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={onPick}
            data-testid={`${testid}-input`}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid={`${testid}-pick`}
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:border-[#E50000] hover:text-[#E50000] transition-colors disabled:opacity-50"
          >
            <UploadSimple size={14} weight="bold" />
            {uploading ? "Ανεβαίνει..." : "Ανέβασμα αρχείου"}
          </button>
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="ή επικόλλησε URL εδώ"
            data-testid={`${testid}-url`}
            className="dash-input text-xs"
          />
        </div>
      </div>
    </div>
  );
}
